import React from "react";

import { useMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import { sortBy } from "lodash";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { MealKind } from "@/gql/graphql";
import useFragment from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { formatISODateTime } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import MealComment, { mealCommentFragment } from "./MealComment";
import MealCommentField from "./MealCommentField";
import { useCreateMeal } from "../utils/useCreateMeal";

interface Props {
  mealId?: string;
  coachId?: string;
  createMealInput?: {
    clientId: string;
    mealType: MealKind;
    consumedOn: string;
    consumedAt: string;
  };
}

const mealCommentsFragment = graphql(`
  fragment MealComments on Meal {
    id
    mealComments {
      ...ClientMealComment
    }
  }
`);

const authorFragment = graphql(`
  fragment NewCommentAuthor on Query {
    coachViewer {
      id
      profile {
        id
        fullName
        avatarUrl(variant: THUMB)
      }
    }
  }
`);

const createMealCommentMutation = graphql(`
  mutation CreateMealComment2($input: CreateMealCommentInput!) {
    createMealComment(input: $input) {
      mealComment {
        ...ClientMealComment
      }
    }
  }
`);

const MealComments: React.FC<Props> = ({
  mealId,
  coachId,
  createMealInput,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [data] = useFragment(mealCommentsFragment, `Meal:${mealId}`);
  const [authorData] = useFragment(authorFragment, "ROOT_QUERY");
  const comments = data?.mealComments ?? [];
  const createMeal = useCreateMeal();
  const [createMealComment] = useMutation(createMealCommentMutation);
  const createComment = async (comment: string) => {
    try {
      let mealIdYo = mealId;
      if (mealIdYo == null) {
        const createdMeal = await createMeal({
          clientId: createMealInput?.clientId,
          mealType: createMealInput?.mealType.toLowerCase(),
          consumedOn: createMealInput?.consumedOn,
          consumedAt: createMealInput?.consumedAt,
        });
        mealIdYo = createdMeal.id;
      }

      await createMealComment({
        variables: { input: { mealId: mealIdYo ?? "", body: comment } },
        optimisticResponse: {
          createMealComment: {
            mealComment: {
              __typename: "MealComment",
              id: nanoid(),
              body: comment,
              mealId: mealId ?? "",
              author: {
                __typename: "Coach",
                id: authorData?.coachViewer.id ?? "",
                profile: {
                  __typename: "Profile",
                  id: authorData?.coachViewer.profile?.id ?? "",
                  fullName: authorData?.coachViewer.profile?.fullName ?? "",
                  avatarUrl: authorData?.coachViewer.profile?.avatarUrl,
                },
              },
              deletedAt: null,
              createdAt: formatISODateTime(new Date()),
            },
          },
        },
        update(cache, { data }) {
          const comment = data?.createMealComment.mealComment;

          const newCommentRef = cache.writeFragment({
            data: comment,
            fragment: mealCommentFragment,
          });

          cache.modify({
            id: `Meal:${mealIdYo}`,
            fields: {
              mealComments(existingComments) {
                return existingComments.concat(newCommentRef);
              },
            },
          });
        },
      });
    } catch (e) {
      snackbar.open(displayError(e));
    }
  };

  return (
    <section className={classes.root}>
      <Typography className={classes.title}>Comments</Typography>
      <div className={classes.comments}>
        {/* TODO: sort comments in the backend */}
        {sortBy(comments, "createdAt").map((comment) => (
          <MealComment key={comment.id} id={comment.id} coachId={coachId} />
        ))}
      </div>
      <div className={classes.newCommentContainer}>
        <MealCommentField onSubmit={createComment} />
      </div>
    </section>
  );
};

const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: 24,
    backgroundColor: "#fff",
  },
  title: {
    color: "#363E45",
    fontSize: 20,
    fontWeight: 700,
  },
  comments: {
    marginTop: 8,
    "& > * + *": {
      borderTop: "1px solid #EDF0F5",
    },
  },
  newCommentContainer: {
    marginTop: "auto",
    width: "100%",
  },
});

export default MealComments;
